import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { Figure, PersonScopeCard } from '@/pages/cvm/shared';
import { cvmCaseParams, fetchCvmOverview } from '@/services/cvm';
import type { CvmCorpus } from '@/services/types';

export function cvmCasesHref(filters: Parameters<typeof cvmCaseParams>[0]): string {
  const params = cvmCaseParams(filters).toString();
  return params ? `/cvm/cases?${params}` : '/cvm/cases';
}

/**
 * The CVM sanctioning corpus at a glance.
 *
 * One population on every panel: the final observed judgment per case, read
 * from the extrato the Colegiado published. The tiles say how many documents
 * carry each layer (a relator, a parsed decision clause, a text layer, a bound
 * Relatório), because every screen after this one is computed over the layer
 * it needs and a reader should know the size of each before trusting a share.
 * The five refusal cards are the corpus's structural absences, measured per
 * request and never asserted.
 */
export function CvmOverviewPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cvm', 'overview'],
    queryFn: fetchCvmOverview,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) return <SkeletonLoader rows={10} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:overview.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:overview.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:overview.explainer" />
      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-corpus>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cvm:overview.corpus.title')}
        </h2>
        <Answer answer={data.corpus} help={t('cvm:overview.corpus.help')}>
          {(corpus: CvmCorpus) => (
            <>
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Figure
                  label={t('cvm:overview.corpus.cases')}
                  value={number.format(corpus.cases)}
                  href="/cvm/cases"
                  hint={t('cvm:overview.corpus.finalHint', {
                    n: number.format(corpus.final_decisions),
                  })}
                />
                <Figure
                  label={t('cvm:overview.corpus.decisions')}
                  value={number.format(corpus.decisions)}
                  hint={t('cvm:overview.corpus.sourcesHint', {
                    archive: number.format(corpus.from_archive),
                    diario: number.format(corpus.from_diario),
                  })}
                />
                <Figure
                  label={t('cvm:overview.corpus.sessions')}
                  value={number.format(corpus.sessions)}
                  href="/cvm/sessions"
                />
                <Figure
                  label={t('cvm:overview.corpus.withRelator')}
                  value={number.format(corpus.with_relator)}
                  href="/cvm/directors"
                  hint={t('cvm:overview.corpus.ofDecisions', {
                    n: number.format(corpus.decisions),
                  })}
                />
              </dl>
              <dl className="mt-2 grid grid-cols-3 gap-2">
                <Figure
                  label={t('cvm:overview.corpus.withClause')}
                  value={number.format(corpus.with_decision_clause)}
                  hint={t('cvm:overview.corpus.clauseHint', {
                    share: percent.format(corpus.clause_coverage),
                  })}
                />
                <Figure
                  label={t('cvm:overview.corpus.withText')}
                  value={number.format(corpus.with_text_layer)}
                  href="/cvm/decisions"
                  hint={t('cvm:overview.corpus.ofDecisions', {
                    n: number.format(corpus.decisions),
                  })}
                />
                <Figure
                  label={t('cvm:overview.corpus.withRelatorio')}
                  value={number.format(corpus.with_relatorio)}
                  href="/cvm/decisions?segment=relatorio"
                  hint={t('cvm:overview.corpus.ofDecisions', {
                    n: number.format(corpus.decisions),
                  })}
                />
              </dl>
            </>
          )}
        </Answer>
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <OutcomeMixPanel
          answer={data.outcome_mix}
          titleKey="cvm:overview.outcomeMix.title"
          helpKey="cvm:overview.outcomeMix.help"
          labelPrefix="cvm:outcomes."
          multiLabelKey="cvm:overview.outcomeMix.multiLabel"
          rowHref={(code) => cvmCasesHref({ outcome: code })}
        />
        <OutcomeMixPanel
          answer={data.sanction_mix}
          titleKey="cvm:overview.sanctionMix.title"
          helpKey="cvm:overview.sanctionMix.help"
          labelPrefix="cvm:sanctionTypes."
          rowHref={(code) => cvmCasesHref({ sanction_type: code })}
        />
        <OutcomeMixPanel
          answer={data.vote_mode_mix}
          titleKey="cvm:overview.voteModeMix.title"
          helpKey="cvm:overview.voteModeMix.help"
          labelPrefix="cvm:voteModes."
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <FineDistributionPanel
          answer={data.fine_distribution}
          titleKey="cvm:overview.fines.title"
          helpKey="cvm:overview.fines.help"
          countKey="cvm:overview.fines.count"
        />
        <section className="rounded-lg border bg-card p-4" data-by-year>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('cvm:overview.byYear.title')}
          </h2>
          <p className="mb-2 text-xs text-muted-foreground">{t('cvm:overview.byYear.help')}</p>
          <div className="max-h-72 overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('cvm:overview.byYear.year')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cvm:overview.byYear.judgments')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.by_year.map((row) => (
                  <tr key={row.year} className="border-t">
                    <td className="px-3 py-1.5 tabular-nums text-foreground">
                      <Link
                        to={cvmCasesHref({ year: row.year })}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {row.year}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
                      {number.format(row.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <PersonScopeCard answer={data.person_scope} />

      <div className="grid gap-4 lg:grid-cols-2">
        <RefusalCard title={t('cvm:overview.refusals.collection')} answer={data.collection} />
        <RefusalCard title={t('cvm:overview.refusals.levy')} answer={data.levy} />
        <RefusalCard title={t('cvm:overview.refusals.identity')} answer={data.identity} />
        <RefusalCard title={t('cvm:overview.refusals.propositions')} answer={data.propositions} />
        {/* Answered while no document lacks a text layer (the corpus tile
            already prints the coverage); the card exists only for the day a
            scanned PDF is loaded and the backend refuses with its count. */}
        {data.text_layer.substrate === 'available' ? null : (
          <RefusalCard
            title={t('cvm:overview.refusals.textLayer')}
            answer={data.text_layer}
            elsewhere={
              <Link to="/cvm/decisions" className="text-primary underline-offset-2 hover:underline">
                {t('cvm:overview.refusals.textLayerElsewhere')}
              </Link>
            }
          />
        )}
      </div>

      <FieldGuide i18nKey="cvm:overview.guide" />
    </div>
  );
}
